import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Linkedin, Send, MapPin, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { CONTACT } from "@/data/site";
import { Logo } from "@/components/brand/Logo";
import { toast } from "sonner";

const COLUMNS = [
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Agents", to: "/agents" },
      { label: "Developers", to: "/developers" },
      { label: "Blog", to: "/blog" },
      { label: "Invest", to: "/invest" },
    ],
  },
  {
    title: "Explore",
    links: [
      { label: "Buy", to: "/buy" },
      { label: "Rent", to: "/rent" },
      { label: "Land", to: "/land" },
      { label: "Commercial", to: "/commercial" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Help Center", to: "/help" },
      { label: "Contact", to: "/contact" },
      { label: "FAQ", to: "/faq" },
      { label: "List Property", to: "/list-property" },
    ],
  },
];

export const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    toast.success("You're subscribed to ABAY360 insights!");
    setEmail("");
  };

  return (
    <footer className="border-t border-border bg-muted/40" data-testid="site-footer">
      <div className="abay-container py-16">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center rounded-xl px-3 py-1.5 shadow-[var(--shadow-sm)]" style={{ backgroundColor: "#001A42" }}>
              <Logo variant="dark" className="h-8 w-auto" />
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              Ethiopia&apos;s smartest AI-powered property platform. Find, buy, rent, sell and invest with confidence.
            </p>
            <div className="mt-5 space-y-2 text-sm text-muted-foreground">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0 text-[#1E3A8A]" /> {CONTACT.address}</p>
              <a href={CONTACT.emailHref} data-testid="footer-email" className="flex items-center gap-2 transition-colors hover:text-[#1E3A8A]"><Mail className="h-4 w-4 shrink-0 text-[#1E3A8A]" /> {CONTACT.email}</a>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-2 transition-colors hover:text-[#1E3A8A]"><Phone className="h-4 w-4 shrink-0 text-[#1E3A8A]" /> {CONTACT.phone}</a>
              <a href={CONTACT.telegramHref} target="_blank" rel="noreferrer" data-testid="footer-telegram" className="flex items-center gap-2 transition-colors hover:text-[#1E3A8A]"><Send className="h-4 w-4 shrink-0 text-[#1E3A8A]" /> {CONTACT.telegram}</a>
            </div>
            <div className="mt-5 flex items-center gap-2">
              {[Facebook, Instagram, Twitter, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="social link"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:border-[#1E3A8A]/30 hover:text-[#1E3A8A] dark:hover:text-[#7AA0FF]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="text-sm font-semibold text-foreground">{col.title}</h4>
              <ul className="mt-4 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link to={link.to} className="text-sm text-muted-foreground transition-colors hover:text-[#1E3A8A] dark:hover:text-[#7AA0FF]">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold text-foreground">Newsletter</h4>
            <p className="mt-4 text-sm text-muted-foreground">Get market insights & new listings.</p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="rounded-xl bg-card"
              />
              <Button type="submit" size="icon" data-testid="footer-newsletter-submit" className="rounded-xl bg-[#1E3A8A] hover:bg-[#172D6B]">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        <Separator className="my-8" />
        <div className="flex flex-col items-center justify-between gap-3 text-sm text-muted-foreground sm:flex-row">
          <p>© {new Date().getFullYear()} ABAY360. All rights reserved.</p>
          <p className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/privacy" className="hover:text-[#1E3A8A]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#1E3A8A]">Terms</Link>
            <Link to="/cookies" className="hover:text-[#1E3A8A]">Cookies</Link>
            <Link to="/safety" className="hover:text-[#1E3A8A]">Safety</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};
